// 日志终端组装入口 / Log terminal assembly entry.
// 装配 tab 条 + per-tab 状态条 + 正文区 + 密度开关；订阅 buffer 按 tab 渲染；
// 监听 ripple:mode-changed 同步 A/B tab 显示；调 bootstrapBinder 启动状态机。
// Wires tabs + status bar + body + density toggle; subscribes to buffer changes;
// listens for mode changes; boots simulation-binder.

import { simulationsApi } from "@/api/simulations";
import { buildApiUrl } from "@/api/client";
import { SseClient } from "@/lib/sse";
import { state as store } from "@/state/store";
import { createLoadingBlock } from "@/lib/spinner";
import type { Mode } from "@/lib/types";

import { renderEntryRow } from "@/features/log-terminal/terminal-renderer";
import {
  bootstrapBinder,
  subscribeBinding,
  type BinderDeps,
} from "@/features/log-terminal/simulation-binder";
import {
  createStatusBar,
  updateStatusBar,
} from "@/features/log-terminal/terminal-status";
import { createTerminalTabs } from "@/features/log-terminal/terminal-tabs";
import {
  getEntries,
  subscribe,
} from "@/features/log-terminal/terminal-buffer";
import type {
  BindingState,
  SseEvent,
  TabState,
  TerminalDensity,
  TerminalTab,
} from "@/features/log-terminal/terminal-types";

interface InitOptions {
  initialMode?: Mode;
  /** Test-only dependency injection; defaults to real API + SSE */
  deps?: BinderDeps;
}

// Per-tab state kept in module closure; re-rendered when binder transitions.
const TAB_STATE: Record<TerminalTab, TabState> = {
  system: vacantState(),
  planA: vacantState(),
  planB: vacantState(),
};

let density: TerminalDensity = "compact";
let activeTab: TerminalTab = "system";
let tabsHandle: ReturnType<typeof createTerminalTabs> | null = null;
let statusRoot: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let densityBtn: HTMLButtonElement | null = null;
let autoScroll = true;

/** 初始化日志终端 / Initialize the log terminal. */
export async function initLogTerminal(opts: InitOptions = {}): Promise<void> {
  const root = document.getElementById("log-terminal");
  if (!root) return;
  const initialMode = opts.initialMode ?? store.mode;

  // Assemble DOM
  root.replaceChildren();
  const header = document.createElement("header");
  header.className = "terminal-header";
  tabsHandle = createTerminalTabs({
    initialMode,
    onChange: (tab) => setActiveTab(tab),
  });
  densityBtn = buildDensityToggle();
  header.append(tabsHandle.root, densityBtn);

  const statusHandle = createStatusBar();
  statusRoot = statusHandle.root;

  bodyEl = document.createElement("div");
  bodyEl.className = "terminal-body";

  root.append(header, statusRoot, bodyEl);

  // Subscribe to buffer updates
  for (const tab of ["system", "planA", "planB"] as TerminalTab[]) {
    subscribe(tab, () => renderTabIfActive(tab));
  }
  subscribeBinding((b) => applyBindingToState(b));

  // Loading placeholder
  bodyEl.replaceChildren(createLoadingBlock("Loading recent simulation logs..."));

  const deps = opts.deps ?? buildDefaultDeps();
  await bootstrapBinder(deps);

  // Mode changes
  window.addEventListener("ripple:mode-changed", (ev) => {
    const mode = (ev as CustomEvent<{ mode: Mode }>).detail.mode;
    tabsHandle!.setMode(mode);
    if (mode === "single" && activeTab === "planB") {
      setActiveTab("planA");
    }
  });

  // Initial render of current tab
  renderTabIfActive(activeTab);
}

/**
 * Switch the active tab programmatically (e.g. when a new simulation starts).
 * Exposed so task-detail / demo-detail can call into the terminal.
 */
export function setActiveTab(tab: TerminalTab): void {
  activeTab = tab;
  tabsHandle?.setActive(tab);
  if (statusRoot) updateStatusBar(statusRoot, TAB_STATE[tab]);
  renderTabIfActive(tab);
}

// ────────────────────────── internals ──────────────────────────

function vacantState(): TabState {
  return {
    lamp: "idle",
    phase: null,
    phaseLabel: null,
    wave: null,
    totalWaves: null,
    round: null,
    totalRounds: null,
    progress: 0,
    message: null,
    unread: false,
  };
}

function buildDensityToggle(): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "terminal-density-toggle";
  btn.textContent = "Compact";
  btn.addEventListener("click", () => {
    density = density === "compact" ? "verbose" : "compact";
    btn.textContent = density === "compact" ? "Compact" : "Full";
    btn.classList.toggle("verbose", density === "verbose");
    renderTabIfActive(activeTab);
  });
  return btn;
}

function renderTabIfActive(tab: TerminalTab): void {
  if (tab !== activeTab || !bodyEl) return;
  const entries = getEntries(tab).filter((e) =>
    density === "verbose" ? true : e.density === "compact",
  );
  if (entries.length === 0) {
    bodyEl.replaceChildren(buildPlaceholder(tab));
    return;
  }
  const frag = document.createDocumentFragment();
  for (const e of entries) frag.append(renderEntryRow(e));
  const pre = document.createElement("div");
  pre.className = "terminal-log";
  pre.append(frag);
  bodyEl.replaceChildren(pre);
  if (autoScroll) pre.scrollTop = pre.scrollHeight;
}

function buildPlaceholder(tab: TerminalTab): HTMLElement {
  const div = document.createElement("div");
  div.className = "terminal-placeholder";
  div.textContent =
    tab === "system"
      ? "System log is empty."
      : "No simulation history yet. Start a simulation to see live logs here.";
  return div;
}

function applyBindingToState(b: BindingState): void {
  if (b.kind === "loading") {
    for (const tab of ["system", "planA", "planB"] as TerminalTab[]) {
      TAB_STATE[tab] = { ...TAB_STATE[tab], message: "Loading", lamp: "queued" };
    }
  }
  if (b.kind === "bound_live") {
    TAB_STATE.planA = { ...TAB_STATE.planA, lamp: "running", message: null };
    if (b.mode === "ab") {
      TAB_STATE.planB = { ...TAB_STATE.planB, lamp: "running", message: null };
    }
  }
  if (b.kind === "bound_report") {
    TAB_STATE.system = { ...TAB_STATE.system, lamp: "running", message: "Generating report" };
    TAB_STATE.planA = { ...TAB_STATE.planA, lamp: "completed", message: null };
    if (b.mode === "ab") {
      TAB_STATE.planB = { ...TAB_STATE.planB, lamp: "completed", message: null };
    }
  }
  if (b.kind === "bound_history") {
    const lamp: TabState["lamp"] =
      b.status === "completed" ? "completed"
      : b.status === "failed" || b.status === "timeout" ? "failed"
      : b.status === "cancelled" ? "cancelled"
      : "idle";
    TAB_STATE.planA = { ...TAB_STATE.planA, lamp, message: null };
    TAB_STATE.planB = { ...TAB_STATE.planB, lamp, message: null };
  }
  if (b.kind === "vacant") {
    // System tab stays idle; plan tabs have no meaningful state.
    TAB_STATE.system = { ...TAB_STATE.system, message: null, lamp: "idle" };
    TAB_STATE.planA = { ...TAB_STATE.planA, message: null, lamp: "idle" };
    TAB_STATE.planB = { ...TAB_STATE.planB, message: null, lamp: "idle" };
    // Vacant: 初次进入默认落在 Plan A，提示用户启动仿真。
    // Vacant on initial entry: default to Plan A so the placeholder prompts action.
    if (activeTab === "system") {
      setActiveTab("planA");
      return;
    }
  }
  if (statusRoot) updateStatusBar(statusRoot, TAB_STATE[activeTab]);
  tabsHandle?.setLamp("system", TAB_STATE.system.lamp);
  tabsHandle?.setLamp("planA", TAB_STATE.planA.lamp);
  tabsHandle?.setLamp("planB", TAB_STATE.planB.lamp);
}

function buildDefaultDeps(): BinderDeps {
  const knownTypes = [
    "progress.phase_start",
    "progress.phase_end",
    "progress.wave_start",
    "progress.wave_end",
    "progress.round_start",
    "progress.round_end",
    "progress.agent_activated",
    "progress.agent_responded",
    "run.starting",
    "run.created",
    "run.completed",
    "run.failed",
    "run.cancelled",
    "run.timeout",
    "report.started",
    "report.regenerated",
    "report.failed",
    "snapshot",
  ];
  return {
    fetchActive: () => simulationsApi.active(),
    // simulationsApi.get returns { ok: true; simulation: SimulationDetail };
    // the real payload also has .timeline inline (see _row_to_detail); cast via unknown keeps the type signature honest.
    fetchDetail: (id) =>
      simulationsApi.get(id) as unknown as ReturnType<BinderDeps["fetchDetail"]>,
    openSse: (id, handlers) => {
      const url = buildApiUrl(`/api/v1/simulations/${encodeURIComponent(id)}/events`);
      const perType: Record<string, (e: SseEvent) => void> = {};
      for (const t of knownTypes) perType[t] = (e) => handlers.onEvent(e);
      const client = new SseClient<SseEvent>(url, perType);
      client.connect();
      return { close: () => client.close() };
    },
  };
}
